import { useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "../api/util";
import { UserQueryContext } from "../context/UserContext";
import { cprQueryEnabled } from "../util/cprAccess";

/**
 * Henter personens CPR-nummer, men kun for brugere med CprRead (ADV-349). Uden CprRead
 * sendes der aldrig et kald (backenden ville svare 403): `cpr` er undefined, `cprLoading`
 * false og `invalidateCpr` gør ingenting.
 */
function usePersonCprNumber(id: string, toggleCpr: boolean) {
  const { user, hasPermission } = useContext(UserQueryContext);
  const canReadCpr = hasPermission(user, "CprRead");
  const enabled = cprQueryEnabled(canReadCpr, !!toggleCpr);
  const { data, isPending, refetch } = useQuery({
    queryKey: ["cpr", id, toggleCpr],
    queryFn: async function () {
      const resp = await get<string>({ query: `person/${id}/cpr` });
      return resp;
    },
    staleTime: Infinity,
    enabled,
  });

  return {
    canReadCpr,
    cpr: canReadCpr ? data : undefined,
    // En slået-fra query står som pending i react-query v5; uden CprRead må det ikke blive
    // en evig spinner i wizards.
    cprLoading: canReadCpr && isPending,
    // refetch() ignorerer `enabled`, så den skal gates eksplicit.
    invalidateCpr: canReadCpr ? refetch : () => Promise.resolve(undefined),
  };
}

export default usePersonCprNumber;
